import { request } from "$/lib/api"
import {
  CreateDamperBrand,
  DamperBrand,
  DamperServiceActionName,
  DamperServicePrice,
  Route,
  UpsertDamperServiceActionName,
  UpsertDamperServicePrice,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

// ================= API Functions =================

async function fetchDamperBrands(): Promise<DamperBrand[]> {
  try {
    return await request<DamperBrand[]>({
      url: Route.DAMPER_BRAND__FIND_ALL,
      method: "GET",
    })
  } catch (error) {
    console.error("Failed to fetch damper brands:", error)
    throw error
  }
}

async function createDamperBrand(
  data: CreateDamperBrand,
): Promise<DamperBrand> {
  return await request<DamperBrand>({
    url: Route.DAMPER_BRAND__SAVE,
    method: "POST",
    data,
  })
}

async function fetchDamperServicePrices(
  storeId: string,
): Promise<DamperServicePrice[]> {
  try {
    return await request<DamperServicePrice[]>({
      url: populateRoute(Route.DAMPER_SERVICE_PRICE__FIND_ALL, storeId),
      method: "GET",
    })
  } catch (error) {
    console.error("Failed to fetch damper service prices:", error)
    throw error
  }
}

async function upsertDamperServicePrice(
  storeId: string,
  data: UpsertDamperServicePrice,
): Promise<DamperServicePrice> {
  return await request<DamperServicePrice>({
    url: populateRoute(Route.DAMPER_SERVICE_PRICE__UPSERT, storeId),
    method: "PUT",
    data,
  })
}

async function deleteDamperServicePrice(priceId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.DAMPER_SERVICE_PRICE__DELETE, priceId),
    method: "DELETE",
  })
}

async function fetchDamperServiceActionNames(
  storeId: string,
): Promise<DamperServiceActionName[]> {
  try {
    return await request<DamperServiceActionName[]>({
      url: populateRoute(Route.DAMPER_SERVICE_ACTION_NAME__FIND_ALL, storeId),
      method: "GET",
    })
  } catch (error) {
    console.error("Failed to fetch damper service action names:", error)
    throw error
  }
}

async function upsertDamperServiceActionName(
  storeId: string,
  data: UpsertDamperServiceActionName,
): Promise<DamperServiceActionName> {
  return await request<DamperServiceActionName>({
    url: populateRoute(Route.DAMPER_SERVICE_ACTION_NAME__UPSERT, storeId),
    method: "PUT",
    data,
  })
}

async function deleteDamperServiceActionName(nameId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.DAMPER_SERVICE_ACTION_NAME__DELETE, nameId),
    method: "DELETE",
  })
}

async function fetchSupportedDamperBrands(
  storeId: string,
): Promise<DamperBrand[]> {
  try {
    return await request<DamperBrand[]>({
      url: populateRoute(Route.STORE_SUPPORTED_DAMPER_BRAND__FIND_ALL, storeId),
      method: "GET",
    })
  } catch (error) {
    console.error("Failed to fetch supported damper brands:", error)
    throw error
  }
}

async function addSupportedDamperBrand(
  storeId: string,
  damperBrandId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.STORE_SUPPORTED_DAMPER_BRAND__ADD, storeId),
    method: "POST",
    data: { damperBrandId },
  })
}

async function removeSupportedDamperBrand(
  storeId: string,
  damperBrandId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.STORE_SUPPORTED_DAMPER_BRAND__REMOVE,
      storeId,
      damperBrandId,
    ),
    method: "DELETE",
  })
}

// ================= Query Options =================

const damperBrandQueries = {
  all: () => ["damper-brand"],
  lists: () => [...damperBrandQueries.all(), "list"],
  list: () =>
    queryOptions({
      queryKey: [...damperBrandQueries.lists()],
      queryFn: () => fetchDamperBrands(),
    }),
}

interface StoreSupportedDamperBrandFilters {
  storeId: string
}

const storeSupportedDamperBrandQueries = {
  all: () => ["store-supported-damper-brand"],
  lists: () => [...storeSupportedDamperBrandQueries.all(), "list"],
  list: (filters: StoreSupportedDamperBrandFilters) =>
    queryOptions({
      queryKey: [...storeSupportedDamperBrandQueries.lists(), filters],
      queryFn: () => fetchSupportedDamperBrands(filters.storeId),
    }),
}

interface DamperServicePriceFilters {
  storeId: string
}

const damperServicePriceQueries = {
  all: () => ["damper-service-price"],
  lists: () => [...damperServicePriceQueries.all(), "list"],
  list: (filters: DamperServicePriceFilters) =>
    queryOptions({
      queryKey: [...damperServicePriceQueries.lists(), filters],
      queryFn: () => fetchDamperServicePrices(filters.storeId),
    }),
}

interface DamperServiceActionNameFilters {
  storeId: string
}

const damperServiceActionNameQueries = {
  all: () => ["damper-service-action-name"],
  lists: () => [...damperServiceActionNameQueries.all(), "list"],
  list: (filters: DamperServiceActionNameFilters) =>
    queryOptions({
      queryKey: [...damperServiceActionNameQueries.lists(), filters],
      queryFn: () => fetchDamperServiceActionNames(filters.storeId),
    }),
}

// ================= Mutations =================

export function useDamperBrandMutations() {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(damperBrandQueries.list())

  const createMutation = useMutation({
    mutationFn: (data: CreateDamperBrand) => createDamperBrand(data),
    onSettled: invalidateQueries,
  })

  return { createMutation }
}

export function useStoreSupportedDamperBrandMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(
      storeSupportedDamperBrandQueries.list({ storeId }),
    )
    // Also invalidate prices since they depend on supported brands
    await queryClient.invalidateQueries(
      damperServicePriceQueries.list({ storeId }),
    )
  }

  const addMutation = useMutation({
    mutationFn: (damperBrandId: string) =>
      addSupportedDamperBrand(storeId, damperBrandId),
    onSettled: invalidateQueries,
  })

  const removeMutation = useMutation({
    mutationFn: (damperBrandId: string) =>
      removeSupportedDamperBrand(storeId, damperBrandId),
    onSettled: invalidateQueries,
  })

  return { addMutation, removeMutation }
}

export function useDamperServicePriceMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(
      damperServicePriceQueries.list({ storeId }),
    )

  const upsertMutation = useMutation({
    mutationFn: (data: UpsertDamperServicePrice) =>
      upsertDamperServicePrice(storeId, data),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (priceId: string) => deleteDamperServicePrice(priceId),
    onSettled: invalidateQueries,
  })

  return { upsertMutation, deleteMutation }
}

export function useDamperServiceActionNameMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(
      damperServiceActionNameQueries.list({ storeId }),
    )

  const upsertMutation = useMutation({
    mutationFn: (data: UpsertDamperServiceActionName) =>
      upsertDamperServiceActionName(storeId, data),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (nameId: string) => deleteDamperServiceActionName(nameId),
    onSettled: invalidateQueries,
  })

  return { upsertMutation, deleteMutation }
}

export {
  damperBrandQueries,
  storeSupportedDamperBrandQueries,
  damperServicePriceQueries,
  damperServiceActionNameQueries,
}
